// Mirrors EL-App/app/utils/apiHeaders.ts and EL-App/app/consts/consts.ts:18.
// Plain fetch + bearer token + ngrok-skip-browser-warning, same as RN.

const LOCAL_DEV_FALLBACK = "http://localhost:3010";
const DEV_API = "https://devapi.eleetz.com";

// When no explicit REACT_APP_API_BASE is baked into the build, pick a sensible
// default from the host we're served on: any deployed eleetz.com host (e.g.
// devapp.eleetz.com) talks to the dev API, which tracks the latest backend;
// local dev hits the local backend. An explicit env var always wins.
function defaultApiBase(): string {
  if (
    typeof window !== "undefined" &&
    window.location.hostname.endsWith("eleetz.com")
  ) {
    return DEV_API;
  }
  return LOCAL_DEV_FALLBACK;
}

export const API_BASE: string =
  process.env.REACT_APP_API_BASE ?? defaultApiBase();

export const UPLOADS_BASE: string =
  process.env.REACT_APP_UPLOADS_BASE_URL ?? "https://uploads.eleetz.com/";

/** `fetch`'s options plus one of our own: `onUploadProgress` is told how much of the request BODY
 * has been sent (0..1). `fetch` cannot report that, so a request that asks for it goes out through
 * XMLHttpRequest instead; everything else about the call (auth, errors, parsing) is the same. */
export type ApiRequestInit = RequestInit & { onUploadProgress?: (fraction: number) => void };

export type ApiFetch = <T = unknown>(
  path: string,
  init?: ApiRequestInit
) => Promise<T>;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

function buildHeaders(
  token: string | null | undefined,
  extra?: HeadersInit
): HeadersInit {
  const headers: Record<string, string> = {
    "ngrok-skip-browser-warning": "1",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (extra) Object.assign(headers, extra as Record<string, string>);
  return headers;
}

// Collapses concurrent identical GETs into a single network request. Guards against React 18
// StrictMode double-invoking effects in dev (and any accidental double-fire) so we don't send the
// same heavy request twice. Entries are removed as soon as the request settles (in-flight only).
const inflightGets = new Map<string, Promise<unknown>>();

export function makeApiFetch(
  getToken: () => Promise<string | null>,
  on401?: () => void
): ApiFetch {
  return function apiFetch<T = unknown>(
    path: string,
    init?: ApiRequestInit
  ): Promise<T> {
    const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
    const method = (init?.method ?? "GET").toUpperCase();
    const hasBody = init?.body != null;
    const dedupe = method === "GET" && !hasBody;
    const key = `${method} ${url}`;

    if (dedupe) {
      const existing = inflightGets.get(key);
      if (existing) return existing as Promise<T>;
    }

    const promise = (async (): Promise<T> => {
      try {
        const token = await getToken();

        const baseExtra: Record<string, string> = {};
        if (hasBody && !(init?.body instanceof FormData)) {
          baseExtra["Content-Type"] = "application/json";
          baseExtra["Accept"] = "application/json";
        } else if (method === "GET") {
          baseExtra["Content-Type"] = "application/json";
        }

        const headers = buildHeaders(token, { ...baseExtra, ...(init?.headers as any) });
        if (init?.onUploadProgress && hasBody) {
          const sent = await sendWithUploadProgress(url, method, headers, init.body as XMLHttpRequestBodyInit, init.onUploadProgress);
          if (sent.status === 401) {
            on401?.();
            throw new ApiError(401, null, "Unauthorized");
          }
          if (sent.status < 200 || sent.status >= 300) throw new ApiError(sent.status, parseBody(sent.text));
          return parseBody(sent.text) as T;
        }

        const { onUploadProgress: _unused, ...fetchInit } = init ?? {};
        const res = await fetch(url, {
          ...fetchInit,
          method,
          headers,
        });

        if (res.status === 401) {
          on401?.();
          throw new ApiError(401, null, "Unauthorized");
        }
        if (!res.ok) {
          let body: unknown = null;
          try {
            body = await res.json();
          } catch {
            try {
              body = await res.text();
            } catch {}
          }
          throw new ApiError(res.status, body);
        }
        if (res.status === 204) return undefined as unknown as T;

        // Some endpoints return text/plain even with JSON content; try JSON first.
        const text = await res.text();
        if (!text) return undefined as unknown as T;
        try {
          return JSON.parse(text) as T;
        } catch {
          return text as unknown as T;
        }
      } finally {
        if (dedupe) inflightGets.delete(key);
      }
    })();

    if (dedupe) inflightGets.set(key, promise);
    return promise;
  };
}

/** JSON when it parses, the text otherwise, undefined when empty — the same rule the fetch path applies. */
function parseBody(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** One request through XMLHttpRequest, for the upload-progress events `fetch` does not have. */
function sendWithUploadProgress(
  url: string,
  method: string,
  headers: HeadersInit,
  body: XMLHttpRequestBodyInit,
  onProgress: (fraction: number) => void,
): Promise<{ status: number; text: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    Object.entries(headers as Record<string, string>).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(Math.min(1, e.loaded / e.total));
    };
    xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText ?? "" });
    xhr.onerror = () => reject(new TypeError("Network request failed"));
    xhr.onabort = () => reject(new TypeError("Network request aborted"));
    xhr.send(body);
  });
}
